<template>
    <div class="tw-container tw-mx-auto tw-p-6 tw-max-w-6xl">
        <h1 class="tw-text-3xl tw-font-bold tw-mb-6">Meeting API Test</h1>

        <!-- Input Fields Section -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow tw-p-6 tw-mb-6">
            <h2 class="tw-text-xl tw-font-semibold tw-mb-4">Test Parameters</h2>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">Meeting ID</label>
                    <input 
                        v-model="formData.meetingId" 
                        type="text" 
                        placeholder="Enter meeting UUID"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">User ID</label>
                    <input 
                        v-model="formData.userId" 
                        type="text" 
                        placeholder="Enter user UUID"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">Company ID</label>
                    <input 
                        v-model="formData.companyId" 
                        type="text" 
                        placeholder="Enter company UUID"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">Title</label>
                    <input 
                        v-model="formData.title" 
                        type="text" 
                        placeholder="Meeting title"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">Scheduled At</label>
                    <input 
                        v-model="formData.scheduledAt" 
                        type="datetime-local" 
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
                <div>
                    <label class="tw-block tw-text-sm tw-font-medium tw-mb-2">Duration (minutes)</label>
                    <input 
                        v-model.number="formData.durationMinutes" 
                        type="number" 
                        placeholder="60"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500"
                    />
                </div>
            </div>
        </div>

        <!-- API Endpoints Section -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow tw-p-6 tw-mb-6">
            <h2 class="tw-text-xl tw-font-semibold tw-mb-4">API Endpoints</h2>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
                <!-- Create Member-to-Member Meeting -->
                <a-button 
                    @click="testCreateMemberToMember" 
                    :loading="loading.createMemberToMember"
                    intent="primary"
                    class="tw-w-full"
                >
                    Create Member-to-Member
                </a-button>

                <!-- Create Member-to-Company Meeting -->
                <a-button 
                    @click="testCreateMemberToCompany" 
                    :loading="loading.createMemberToCompany"
                    intent="primary"
                    class="tw-w-full"
                >
                    Create Member-to-Company
                </a-button>

                <!-- List Meetings -->
                <a-button 
                    @click="testPaginate" 
                    :loading="loading.paginate"
                    intent="info"
                    class="tw-w-full"
                >
                    List Meetings
                </a-button>

                <!-- Get Meeting Details -->
                <a-button 
                    @click="testGet" 
                    :loading="loading.get"
                    intent="info"
                    class="tw-w-full"
                >
                    Get Meeting Details
                </a-button>

                <!-- Accept Meeting -->
                <a-button 
                    @click="testAccept" 
                    :loading="loading.accept"
                    intent="success"
                    class="tw-w-full"
                >
                    Accept Meeting
                </a-button>

                <!-- Decline Meeting -->
                <a-button 
                    @click="testDecline" 
                    :loading="loading.decline"
                    intent="warning"
                    class="tw-w-full"
                >
                    Decline Meeting
                </a-button>

                <!-- Start Meeting -->
                <a-button 
                    @click="testStart" 
                    :loading="loading.start"
                    intent="success"
                    class="tw-w-full"
                >
                    Start Meeting
                </a-button>

                <!-- Complete Meeting -->
                <a-button 
                    @click="testComplete" 
                    :loading="loading.complete"
                    intent="success"
                    class="tw-w-full"
                >
                    Complete Meeting
                </a-button>
            </div>
        </div>

        <!-- Response Display Section -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
            <h2 class="tw-text-xl tw-font-semibold tw-mb-4">Response</h2>
            <div class="tw-bg-gray-50 tw-rounded-md tw-p-4 tw-overflow-auto tw-max-h-96">
                <pre class="tw-text-sm tw-whitespace-pre-wrap">{{ response }}</pre>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import meetingsApi from '@/api/meetings';
import useAlert from '@/composables/useAlert';

// Form data
const formData = ref({
    meetingId: '',
    userId: '',
    companyId: '',
    title: 'Test Meeting',
    scheduledAt: '',
    durationMinutes: 60,
    description: '',
    timezone: 'UTC',
    location: 'Virtual Meeting'
});

// Loading states
const loading = ref({
    createMemberToMember: false,
    createMemberToCompany: false,
    paginate: false,
    get: false,
    accept: false,
    decline: false,
    start: false,
    complete: false
});

// Response display
const response = ref('Click a button to test an endpoint...');

// Helper function to format response
const formatResponse = (data) => {
    return JSON.stringify(data, null, 2);
};

// Helper function to handle errors
const handleError = (error, action) => {
    console.error(`Error ${action}:`, error);
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    useAlert(`Failed to ${action}: ${errorMessage}`, 'danger');
    response.value = formatResponse({
        error: errorMessage,
        details: error.response?.data || error.message
    });
};

// Test functions
const testCreateMemberToMember = async () => {
    if (!formData.value.userId) {
        useAlert('Please enter a User ID', 'warning');
        return;
    }

    loading.value.createMemberToMember = true;
    try {
        const data = {
            user_id: formData.value.userId,
            title: formData.value.title || 'Test Meeting',
            description: formData.value.description,
            scheduled_at: formData.value.scheduledAt 
                ? new Date(formData.value.scheduledAt).toISOString()
                : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default: tomorrow
            duration_minutes: formData.value.durationMinutes || 60,
            timezone: formData.value.timezone,
            location: formData.value.location
        };

        const result = await meetingsApi.createMemberToMember(data);
        response.value = formatResponse(result.data);
        useAlert('Member-to-member meeting created successfully!', 'success');
        
        // Update meeting ID if returned
        if (result.data?.data?.id) {
            formData.value.meetingId = result.data.data.id;
        }
    } catch (error) {
        handleError(error, 'create member-to-member meeting');
    } finally {
        loading.value.createMemberToMember = false;
    }
};

const testCreateMemberToCompany = async () => {
    if (!formData.value.companyId) {
        useAlert('Please enter a Company ID', 'warning');
        return;
    }

    loading.value.createMemberToCompany = true;
    try {
        const data = {
            company_id: formData.value.companyId,
            title: formData.value.title || 'Test Meeting',
            description: formData.value.description,
            scheduled_at: formData.value.scheduledAt 
                ? new Date(formData.value.scheduledAt).toISOString()
                : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default: tomorrow
            duration_minutes: formData.value.durationMinutes || 60,
            timezone: formData.value.timezone,
            location: formData.value.location
        };

        const result = await meetingsApi.createMemberToCompany(data);
        response.value = formatResponse(result.data);
        useAlert('Member-to-company meeting created successfully!', 'success');
        
        // Update meeting ID if returned
        if (result.data?.data?.id) {
            formData.value.meetingId = result.data.data.id;
        }
    } catch (error) {
        handleError(error, 'create member-to-company meeting');
    } finally {
        loading.value.createMemberToCompany = false;
    }
};

const testPaginate = async () => {
    loading.value.paginate = true;
    try {
        const params = {
            page: 1,
            per_page: 20
        };

        const result = await meetingsApi.paginate(params);
        response.value = formatResponse(result.data);
        useAlert('Meetings retrieved successfully!', 'success');
    } catch (error) {
        handleError(error, 'list meetings');
    } finally {
        loading.value.paginate = false;
    }
};

const testGet = async () => {
    if (!formData.value.meetingId) {
        useAlert('Please enter a Meeting ID', 'warning');
        return;
    }

    loading.value.get = true;
    try {
        const result = await meetingsApi.get(formData.value.meetingId);
        response.value = formatResponse(result.data);
        useAlert('Meeting details retrieved successfully!', 'success');
    } catch (error) {
        handleError(error, 'get meeting details');
    } finally {
        loading.value.get = false;
    }
};

const testAccept = async () => {
    if (!formData.value.meetingId) {
        useAlert('Please enter a Meeting ID', 'warning');
        return;
    }

    loading.value.accept = true;
    try {
        const result = await meetingsApi.accept(formData.value.meetingId);
        response.value = formatResponse(result.data);
        useAlert('Meeting accepted successfully!', 'success');
    } catch (error) {
        handleError(error, 'accept meeting');
    } finally {
        loading.value.accept = false;
    }
};

const testDecline = async () => {
    if (!formData.value.meetingId) {
        useAlert('Please enter a Meeting ID', 'warning');
        return;
    }

    loading.value.decline = true;
    try {
        const result = await meetingsApi.decline(formData.value.meetingId, {
            reason: 'Test decline reason'
        });
        response.value = formatResponse(result.data);
        useAlert('Meeting declined successfully!', 'success');
    } catch (error) {
        handleError(error, 'decline meeting');
    } finally {
        loading.value.decline = false;
    }
};

const testStart = async () => {
    if (!formData.value.meetingId) {
        useAlert('Please enter a Meeting ID', 'warning');
        return;
    }

    loading.value.start = true;
    try {
        const result = await meetingsApi.start(formData.value.meetingId);
        response.value = formatResponse(result.data);
        useAlert('Meeting started successfully!', 'success');
    } catch (error) {
        handleError(error, 'start meeting');
    } finally {
        loading.value.start = false;
    }
};

const testComplete = async () => {
    if (!formData.value.meetingId) {
        useAlert('Please enter a Meeting ID', 'warning');
        return;
    }

    loading.value.complete = true;
    try {
        const result = await meetingsApi.complete(formData.value.meetingId);
        response.value = formatResponse(result.data);
        useAlert('Meeting completed successfully!', 'success');
    } catch (error) {
        handleError(error, 'complete meeting');
    } finally {
        loading.value.complete = false;
    }
};
</script>

<style scoped>
pre {
    font-family: 'Courier New', monospace;
    line-height: 1.5;
}
</style>
